import { useContext, useEffect, useState } from 'react';

import { useCartWithOffer } from '@hooks/cart/useCartWithOffer';
import { CartContext } from '@context/cart';

import styles from './FinancingDuration.module.scss';
import { OfferItemPricing } from 'types/offer.types';
import { OptionSelector } from '@base-components/OptionSelector/OptionSelector';
import { useUpdateCartItemsWithOffer } from '@hooks/cart/useUpdateCartItemsWithOffer';
import { useRemoveItemsWithOffer } from '@hooks/cart/useRemoveItemsWithOffer';

const getUniquePricings = (pricings: OfferItemPricing[]) => {
  return pricings.filter((pricing, index, self) => self.findIndex(p => p.months === pricing.months) === index);
};

const filterByMainOfferItemCount = (pricings: OfferItemPricing[], mainOfferItemCount: number) => {
  return pricings.filter(pricing => pricings.filter(p => p.months === pricing.months).length === mainOfferItemCount);
};

export const FinancingDuration = ({ updateCart, title, setIsDifferentDuration }) => {
  const { data: cart } = useCartWithOffer();
  const [selectedFinancePricing, setSelectedFinancePricing] = useState<OfferItemPricing | null>(null);
  const updateCartItems = useUpdateCartItemsWithOffer();
  const removeItemsMutation = useRemoveItemsWithOffer();

  const mainOfferItems = cart.items.filter(item => item.offerItem.type === 'main');

  const preSelectedPricings = mainOfferItems.map(item => item.pricing);
  const allSameDurations = preSelectedPricings.every(pricing => pricing.months === preSelectedPricings[0].months);
  const availablePricings = mainOfferItems.flatMap(item =>
    item.offerItem.pricings.filter(pricing => pricing.isAvailable)
  );

  const filteredPricings = getUniquePricings(filterByMainOfferItemCount(availablePricings, mainOfferItems.length)).sort(
    (a, b) => a.months - b.months
  );

  // TODO: refactor - add below useEffect to <CheckoutCart />
  useEffect(() => {
    if (allSameDurations) {
      setIsDifferentDuration(false);
      handlePricingChange(preSelectedPricings[0]);
    } else {
      setIsDifferentDuration(true);
      setSelectedFinancePricing(null);
    }
  }, []);

  const handlePricingChange = async (selectedPricing: OfferItemPricing) => {
    const updatedItems = cart?.items?.map(item => ({
      ...item,
      pricingId: item.offerItem.pricings.find(pricing => pricing.months === selectedPricing.months)?.id,
    }));

    await updateCartItems.mutateAsync({
      items: updatedItems,
    });

    const itemsToRemove = cart?.items.filter(item =>
      item.offerItem.pricings.every(pricing => pricing.months !== selectedPricing.months)
    );

    if (itemsToRemove.length > 0) {
      await removeItemsMutation.mutateAsync(itemsToRemove);
    }

    setIsDifferentDuration(false);
    setSelectedFinancePricing(selectedPricing);
    updateCart();
  };

  return (
    <div>
      <h2 className={styles.title}>{title}</h2>
      <OptionSelector<OfferItemPricing>
        options={filteredPricings}
        handleChange={handlePricingChange}
        selectedOption={selectedFinancePricing}
      />
    </div>
  );
};
